import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type FilterState = {
  [key: string]: boolean;
};

type ClearEntityFiltersContextValue = {
  filters: string[];
  setFilters: (filters: string[]) => void;
  clearFilterState: FilterState;
  clearSpecifiedFilters: (filterKeys: string[]) => void;
  clearAllFilters: () => void;
  resetFilters: (key: string) => void;
};

const ClearEntityFiltersContext = createContext<
  ClearEntityFiltersContextValue | undefined
>(undefined);

export const ClearEntityFiltersProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [clearFilterState, setclearFilterState] = useState<FilterState>({});
  const [filters, setFilters] = useState<string[]>([]);

  const clearAllFilters = useCallback(() => {
    setclearFilterState(prevState =>
      filters.reduce(
        (acc, filterKey) => {
          acc[filterKey] = true;
          return acc;
        },
        { ...prevState },
      ),
    );
  }, [filters]);

  const clearSpecifiedFilters = useCallback(
    (filterKeys: string[]) => {
      const isValid = filterKeys.every(key => filters.includes(key));
      if (!isValid) {
        return;
      }

      setclearFilterState(prevState =>
        filterKeys.reduce(
          (acc, filterKey) => {
            acc[filterKey] = true;
            return acc;
          },
          { ...prevState },
        ),
      );
    },
    [filters],
  );

  const resetFilters = useCallback((key: string) => {
    setclearFilterState(prevState => {
      const newState = { ...prevState };
      newState[key] = false;
      return newState;
    });
  }, []);

  const value = useMemo(
    () => ({
      filters,
      clearFilterState,
      setFilters,
      clearSpecifiedFilters,
      clearAllFilters,
      resetFilters,
    }),
    [
      filters,
      clearFilterState,
      clearSpecifiedFilters,
      clearAllFilters,
      resetFilters,
    ],
  );

  return (
    <ClearEntityFiltersContext.Provider value={value}>
      {children}
    </ClearEntityFiltersContext.Provider>
  );
};

export const useClearEntityFilters = (filters?: string[]) => {
  const context = useContext(ClearEntityFiltersContext);

  if (context === undefined) {
    throw new Error(
      'useClearEntityFilters must be used within a ClearEntityFiltersProvider',
    );
  }

  const {
    setFilters,
    clearAllFilters,
    clearFilterState,
    clearSpecifiedFilters,
    resetFilters,
  } = context;

  useEffect(() => {
    if (filters) {
      setFilters(filters);
    }
  }, [filters, setFilters]);

  return {
    clearAllFilters,
    clearSpecifiedFilters,
    clearFilterState,
    resetFilters,
  };
};
